import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { compatibilityTheme } from './themes/compatibilityTheme';
import { wdsTheme } from './themes/wdsTheme';
import { getSystemThemeMode, getThemeStyleLocalStorage, setThemeStyleLocalStorage, THEME_MODE_STORAGE_KEY, ThemeSetter, } from './themeUtils';
/**
 * A custom storage manager for MUI's ThemeProvider that keeps localStorage
 * get/set but disables the cross-tab `storage` event subscription.
 *
 * WDS already syncs the theme across tabs via its own `themeMode` storage key.
 * If MUI also listens for its `mui-mode` key changes independently, the two
 * systems race: MUI reacts first (switching CSS vars), then WDS catches up a
 * frame later, causing a visible dark→light flicker. By returning a no-op
 * subscribe, we let WDS be the single source of truth for cross-tab sync.
 */
const noSubscribeStorageManager = ({ key, storageWindow, }) => ({
    get(defaultValue) {
        if (typeof window === 'undefined' || !storageWindow)
            return defaultValue;
        try {
            return storageWindow.localStorage.getItem(key) || defaultValue;
        }
        catch (_a) {
            return defaultValue;
        }
    },
    set(value) {
        if (!storageWindow)
            return;
        try {
            storageWindow.localStorage.setItem(key, value);
        }
        catch (_a) {
            // unsupported
        }
    },
    subscribe() {
        // Intentionally a no-op — WDS handles cross-tab sync via THEME_MODE_STORAGE_KEY.
        return () => { };
    },
});
export const WDSThemeContext = createContext({
    isMounted: false,
    setThemeStyle: () => { },
});
export function useWDSThemeContext() {
    const { themeMode, themeStyle, setThemeStyle, isMounted } = useContext(WDSThemeContext);
    return { themeMode, themeStyle, setThemeStyle, isMounted };
}
/**
 * A theme provider for WDS. It will apply the new WDS theme to any wrapped components.
 *
 * If a themeMode isn't provided it will first try to match the user's system settings. If the user doesnt have a theme set, it defaults to light.
 *
 * If a themeMode is provided it will override the user's system settings
 *
 * #### themeMode
 * - `light`: The default light theme
 * - `dark`: The dark theme
 * - `system`: Match the user's system theme
 * - `compatibility`: A theme to make the new components match the old WDS as much as possible
 *
 * ### Example
 * In the below example the theme will not change, it will always be light mode.
 *
 * ```tsx
 * <WDSThemeProvider themeMode="light">
 *     ... wrapped components
 * </WDSThemeProvider>
 * ```
 *
 * ### Documentation
 * - [WDS2 Migration Guide](https://seequent.atlassian.net/wiki/spaces/PLT/pages/570949752/Migration+Guide+web-design-system-2)
 * - [Documentation](https://wds2docs.dev-sqnt.com/?path=/docs/theme-wdsthemeprovider)
 */
export function WDSThemeProvider({ children, themeMode, }) {
    const [isMounted, setIsMounted] = useState(false);
    useEffect(() => {
        setIsMounted(true);
    }, []);
    const [currentThemeMode, setCurrentThemeMode] = useState(() => {
        var _a;
        if (typeof window === 'undefined') {
            // If there is no window, we must be Server-side rendering, so we default to light
            return 'light';
        }
        return (_a = getThemeStyleLocalStorage()) !== null && _a !== void 0 ? _a : 'system';
    });
    // on load, check system default for theme
    useEffect(() => {
        // if we have a themeMode override prop, use that
        if (themeMode) {
            setCurrentThemeMode(themeMode);
        }
    }, [themeMode]);
    useEffect(() => {
        if (currentThemeMode && currentThemeMode !== getThemeStyleLocalStorage()) {
            setThemeStyleLocalStorage(currentThemeMode);
        }
    }, [currentThemeMode]);
    // Sync theme across browser tabs. When another tab writes to the
    // themeMode localStorage key, update our React state to match so
    // that ThemeSetter and MUI's internal cross-tab sync stay aligned
    // (prevents a light↔dark ping-pong flicker between tabs).
    useEffect(() => {
        if (themeMode) {
            // If a fixed themeMode prop is provided, don't sync from storage
            return undefined;
        }
        const handleStorage = (event) => {
            if (event.key === THEME_MODE_STORAGE_KEY) {
                const newValue = event.newValue;
                if (newValue === 'light' || newValue === 'dark') {
                    setCurrentThemeMode(newValue);
                }
                else {
                    setCurrentThemeMode('system');
                }
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [themeMode]);
    let lightOrDark = 'light';
    if (currentThemeMode === 'dark') {
        lightOrDark = 'dark';
    }
    else if (currentThemeMode === 'compatibility') {
        lightOrDark = 'light';
    }
    else if (currentThemeMode === 'system') {
        lightOrDark = getSystemThemeMode();
    }
    const value = useMemo(() => ({
        isMounted,
        themeStyle: currentThemeMode,
        themeMode: lightOrDark,
        setThemeStyle: (newTheme) => {
            setCurrentThemeMode(newTheme);
        },
    }), [isMounted, currentThemeMode]);
    const stableStorageManager = useCallback(noSubscribeStorageManager, []);
    return (_jsx(WDSThemeContext.Provider, { value: value, children: _jsxs(ThemeProvider, { theme: currentThemeMode === 'compatibility' ? compatibilityTheme : wdsTheme, 
            // In MUI v7's CSS vars mode, theme.palette is locked to the default (light)
            // scheme for performance. forceThemeRerender makes theme.palette reflect the
            // active color scheme so that existing theme.palette.mode checks still work.
            forceThemeRerender: true, 
            // Align MUI's initial color scheme with the WDS-resolved mode so
            // the first render already uses the correct palette and CSS class.
            defaultMode: lightOrDark, 
            // Skip the isClient=false→true transition that causes CssVarsProvider
            // to return mode/colorScheme as undefined on the first render,
            // which with forceThemeRerender would flash the light palette.
            noSsr: true, 
            // Disable MUI's own cross-tab storage sync. WDS exclusively
            // manages cross-tab theme changes via the themeMode key and
            // drives MUI through ThemeSetter, preventing race-condition flicker.
            storageManager: stableStorageManager, children: [_jsx(ThemeSetter, { mode: lightOrDark }), _jsx(CssBaseline, { children: children })] }) }));
}
//# sourceMappingURL=WDSThemeProvider.js.map